import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Settings2, CheckCheck } from "lucide-react";

const CAT_DOT = {
  ACTION_REQUIRED: "bg-red-500", MENTION: "bg-violet-500",
  APPROVAL: "bg-amber-500", COMMERCIAL: "bg-emerald-500",
  OPERATIONS: "bg-sky-500", SECURITY: "bg-rose-600", SYSTEM: "bg-slate-400",
};

/** Actionable notification centre content (rendered inside the bell popover). */
export const NotificationCentre = ({ onNavigate }) => {
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/collab/notifications").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPrefs = async () => { navigate("/notifications"); onNavigate?.(); };

  const toggleMute = async (cat) => {
    const muted = prefs.muted.includes(cat)
      ? prefs.muted.filter((c) => c !== cat)
      : [...prefs.muted, cat];
    setPrefs({ ...prefs, muted });
    await api.post("/collab/notifications/prefs", { muted });
    load();
  };

  const markAll = async () => {
    await api.post("/collab/notifications/read-all");
    toast.success("All marked read");
    load();
  };

  const open = async (n) => {
    if (!n.read) api.post("/collab/notifications/read", { ids: [n.id] }).then(load);
    if (n.route) { navigate(n.route); onNavigate?.(); }
  };

  if (showPrefs && prefs) {
    return (
      <div data-testid="notification-prefs">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Preferences</span>
          <button onClick={() => setShowPrefs(false)} className="ml-auto text-[11px] text-slate-500 hover:text-slate-900">← Back</button>
        </div>
        <div className="space-y-1.5">
          {prefs.categories.map((c) => {
            const mandatory = prefs.mandatory.includes(c);
            const muted = prefs.muted.includes(c);
            return (
              <label key={c} className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={!muted} disabled={mandatory}
                  data-testid={`pref-${c.toLowerCase()}`}
                  onChange={() => toggleMute(c)} className="accent-slate-900" />
                <span className={`h-1.5 w-1.5 rounded-full ${CAT_DOT[c]}`} />
                {c.replace(/_/g, " ")}
                {mandatory && <span className="text-[10px] text-slate-400 ml-auto">mandatory</span>}
              </label>
            );
          })}
        </div>
        <div className="text-[10px] text-slate-400 mt-2">Security & action-required notifications cannot be disabled by policy.</div>
      </div>
    );
  }

  return (
    <div data-testid="notification-centre">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-[0.16em] text-slate-400">Notifications</span>
        {data?.unread > 0 && <span className="text-[10px] font-semibold text-white bg-red-500 rounded-full px-1.5">{data.unread}</span>}
        <span className="ml-auto flex gap-2">
          <button onClick={markAll} title="Mark all read" data-testid="notif-mark-all"
            className="text-slate-400 hover:text-slate-800"><CheckCheck className="h-3.5 w-3.5" /></button>
          <button onClick={openPrefs} title="Preferences" data-testid="notif-prefs-btn"
            className="text-slate-400 hover:text-slate-800"><Settings2 className="h-3.5 w-3.5" /></button>
        </span>
      </div>
      {!data || data.items.length === 0 ? (
        <div className="text-[12px] text-slate-400">You're all caught up.</div>
      ) : (
        <div className="max-h-[380px] overflow-y-auto -mx-1 px-1 space-y-1">
          {data.items.map((n) => (
            <button key={n.id} onClick={() => open(n)}
              data-testid={`notif-${n.id}`}
              className={`w-full text-left rounded-md px-2 py-1.5 hover:bg-slate-50 ${n.read ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${CAT_DOT[n.category] || CAT_DOT.SYSTEM}`} />
                <span className="text-[12px] font-medium text-slate-900 truncate">{n.title}</span>
                {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0 ml-auto" />}
              </div>
              {n.change ? (
                <div className="text-[11px] text-slate-600 ml-3 mt-0.5">
                  {n.change.field}: <span className="line-through text-slate-400">{String(n.change.before ?? "—")}</span>
                  <span className="mx-1">→</span>
                  <span className="font-medium">{String(n.change.after ?? "—")}</span>
                </div>
              ) : n.body ? (
                <div className="text-[11px] text-slate-500 ml-3 mt-0.5 line-clamp-2">{n.body}</div>
              ) : null}
              <div className="flex items-center gap-2 ml-3 mt-0.5">
                <span className="text-[10px] text-slate-400">{(n.at || "").slice(0, 16).replace("T", " ")}</span>
                {(n.actions || []).map((a) => (
                  <span key={a.label} className="text-[10px] text-slate-500 border border-slate-200 rounded px-1 py-0.5">{a.label}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationCentre;
